// **画面を消させない仕組みと、背景に回ったことの見える化**（#128 / DOC-0031 の M8）。
//
// なぜ要るか: **スマホのブラウザは、画面を消すと通話が壊れる。**
// iPhone は背景に回るとマイクが止まり「聞き専」になり、ページ側に回避手段が無い
// （DOC-0024 §0。ネイティブの `UIBackgroundModes` が要る）。Android も条件によって
// `AudioWorklet` の処理が間引かれる。**ブラウザに残された手は「画面を点けたままにする」**
// （Screen Wake Lock）だけで、ADR-0017 §1-2 でスマホのブラウザを対応範囲に入れた以上、
// これが無いと「机に置いて通話」が成立しない。
//
// **取れないブラウザがある**（iOS Safari は 18.4 から。16.4〜18.4 は部分対応で、
// ホーム画面に追加した場合は効かない。DOC-0024 §2）。取れないことは異常ではないので、
// **1 回知らせて通話は続ける。**
//
// # プラットフォーム層だけの機能
//
// ネイティブアプリには別の仕組み（iOS は `isIdleTimerDisabled`・Android は
// `FLAG_KEEP_SCREEN_ON`）があるので、**ここの実装は持っていけない**（ADR-0017 §5-3）。
// だから `client/core` には置かず、最小限で作る。

/** 画面の表示を保つ取っ手。ブラウザの `WakeLockSentinel` のうち、ここで使う分だけ。 */
export interface SentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: "release", listener: () => void): void;
}

/** `navigator.wakeLock` のうち、ここで使う分だけ。 */
export interface WakeLockLike {
  request(type: "screen"): Promise<SentinelLike>;
}

export type Log = (level: "info" | "warn", message: string) => void;

/**
 * 画面を消させない係。**通話のあいだだけ取る**（ずっと取ると電池を食う）。
 *
 * # OS が勝手に外すことがある
 *
 * 取った状態は**画面が隠れると自動で解放される**（他のアプリに切り替えた・画面が消えた）。
 * 戻ってきたときに取り直すのはこちらの仕事なので、`onVisible()` を画面の
 * `visibilitychange` から呼ぶ。
 */
export class ScreenKeepAwake {
  private sentinel: SentinelLike | null = null;
  /** 通話中か。これが false のあいだは取り直さない。 */
  private wanted = false;
  /** 「取れない」の知らせは 1 回だけにする（押すたびに鳴ると読めなくなる）。 */
  private warned = false;
  /**
   * **断られたので、次に利用者が画面を触ったら取り直したい**状態か。
   *
   * iOS の Safari は **利用者の操作の直後でないと取らせない**（transient activation が要る）。
   * 画面が隠れて戻ったときの取り直しには操作が伴わないので、そこでは必ず断られる。
   */
  private needsGesture = false;

  constructor(
    private readonly lock: WakeLockLike | undefined,
    private readonly log: Log,
  ) {}

  /** このブラウザで使えるか。 */
  get supported(): boolean {
    return this.lock !== undefined;
  }

  /** いま画面を保っているか。 */
  get held(): boolean {
    return this.sentinel !== null && !this.sentinel.released;
  }

  /** 操作を待っている（断られたので、次に画面を触ったら取り直す）か。 */
  get waitingForGesture(): boolean {
    return this.needsGesture && this.wanted && !this.held;
  }

  /**
   * 通話が始まったので画面を保つ。取れなくても例外は投げない。戻り値は取れたか。
   *
   * **利用者の操作と同じ処理の流れの中から呼ぶこと**（`await` を挟んだ後では遅い）。
   * iOS の Safari は操作の直後でないと取らせず、`NotAllowedError`（Permission was denied）
   * で断る。2026-10-15 の実機（iOS 18.7 / Safari 27.0.1）で実際に断られた。
   */
  async acquire(): Promise<boolean> {
    this.wanted = true;
    if (!this.lock) {
      if (!this.warned) {
        this.warned = true;
        this.log(
          "warn",
          "このブラウザは画面を点けたままにできません（Screen Wake Lock 非対応）。" +
            "画面が消えると通話が止まることがあります",
        );
      }
      return false;
    }
    if (this.held) return true;
    try {
      const sentinel = await this.lock.request("screen");
      // 取ったあとに退室していたら、すぐ返す（取りっぱなしで電池を食わない）
      if (!this.wanted) {
        await sentinel.release().catch(() => {});
        return false;
      }
      this.sentinel = sentinel;
      // OS 側の解放（画面が隠れた・電池が少ない）を拾う。戻ってきたら onVisible が取り直す
      sentinel.addEventListener("release", () => {
        if (this.sentinel === sentinel) this.sentinel = null;
      });
      this.needsGesture = false;
      this.log("info", "画面を点けたままにします（通話のあいだだけ）");
      return true;
    } catch (e) {
      // **断られる理由は 2 つある。** ①利用者の操作の直後でない（iOS の Safari）
      // ②電池が少ない・省電力。①は次に画面を触ったときに取り直せるので、印を立てておく
      this.needsGesture = true;
      if (!this.warned) {
        this.warned = true;
        this.log(
          "warn",
          `画面を点けたままにできませんでした（${e instanceof Error ? e.message : String(e)}）。` +
            "画面を触ったときにもう一度試します。" +
            "取れないままだと、画面が消えたときに通話が止まることがあります",
        );
      }
      return false;
    }
  }

  /** 通話が終わったので返す。 */
  async release(): Promise<void> {
    this.wanted = false;
    const sentinel = this.sentinel;
    this.sentinel = null;
    if (!sentinel || sentinel.released) return;
    await sentinel.release().catch(() => {});
  }

  /**
   * 画面が見える状態に戻ってきた。**通話中なら取り直す。**
   *
   * 自動で解放されたぶんを戻すためで、通話していなければ何もしない。
   */
  async onVisible(): Promise<void> {
    if (this.wanted && !this.held) await this.acquire();
  }

  /**
   * **利用者が画面を触った。** 断られたままなら取り直す。
   *
   * iOS の Safari で、画面が隠れて戻ったときの取り直しが必ず断られる件の受け皿
   * （取り直しには操作が伴わないため）。操作のたびに呼んでよい（要らなければ何もしない）。
   */
  async onUserGesture(): Promise<void> {
    if (this.waitingForGesture) await this.acquire();
  }
}

/**
 * **背景に回ったことを隠さない**（DOC-0031 の M8 の後半）。
 *
 * iPhone は背景でマイクが止まるのに**画面には何も出ない**ので、戻ってきた利用者には
 * 「相手に聞こえていなかった」ことが分からない。**止まる可能性があったことを伝える**のが
 * ここの役目で、本当に止まったかを測っているわけではない（ページからは分からない）。
 *
 * @param hiddenMs 画面が隠れていた時間（ミリ秒）
 */
export function backgroundNotice(hiddenMs: number): string {
  const seconds = Math.round(hiddenMs / 1000);
  return (
    `画面が隠れていました（${seconds} 秒）。` +
    "iPhone は背景に回るとマイクが止まります（その間こちらの声は相手に届きません）。" +
    "机に置いて使うときは画面を消さないでください"
  );
}
